import { useQueryClient } from '@tanstack/react-query';
import { useCallback, useMemo } from 'react';

import QuickActionItem from '@/components/Common/QuickActionItem';
import List from '@/components/List/List';
import Tabs from '@/components/Tab/Tabs';
import type { Tab } from '@/components/Tab/types';
import useTabs from '@/components/Tab/useTabs';
import {
  notificationQueryKeys,
  useNotificationListQuery,
} from '@/entities/notification';
import { getApiErrorMessage } from '@/error-handler/api-error-messages/ApiErrorMessages';
import WorkflowItem from '@/features/Workflow/WorkflowItem';
import useAppInit from '@/hooks/domain/App/useAppInit';
import useWorkspaceRouter from '@/hooks/domain/useWorkspaceRouter';
import { useMessage } from '@/messages';
import service from '@/services/index';
import { PageHeader } from '@/shared/ui';
import { useToast } from '@/stores/pure/ToastStore';
import {
  MAX_WORKFLOW_COUNT,
  WORKFLOW_SUBSCRIPTION_BASIS,
  WORKFLOW_TYPE,
  WorkflowIconMap,
} from '@/types/workflowConst';

const WorkflowList = () => {
  const appInit = useAppInit();
  const wr = useWorkspaceRouter();
  const addTaost = useToast();
  const m = useMessage();
  const workflowTypes = useMemo(
    () => [
      WORKFLOW_TYPE.REALTIME,
      WORKFLOW_TYPE.PERIODIC,
      WORKFLOW_TYPE.SUBSCRIPTION,
      WORKFLOW_TYPE.SIGNAL_ALERT,
    ],
    [],
  );
  const { data } = useNotificationListQuery(workflowTypes, {
    enabled: appInit,
    staleTime: 0,
  });

  const queryClient = useQueryClient();

  const filterFunc = useCallback(
    (type: WORKFLOW_TYPE) => {
      return data?.filter((d) => d.type === type).length || 0;
    },
    [data],
  );

  const tabs: Tab[] = useMemo(() => {
    return [
      {
        text: (
          <span className="text-sm font-semibold">
            All ({data?.length || 0})
          </span>
        ),
        id: 'all',
      },
      {
        text: (
          <span className="text-sm font-semibold">
            New feedback ({filterFunc(WORKFLOW_TYPE.REALTIME)})
          </span>
        ),
        id: WORKFLOW_TYPE.REALTIME,
      },
      {
        text: (
          <span className="text-sm font-semibold">
            Metric change ({filterFunc(WORKFLOW_TYPE.PERIODIC)})
          </span>
        ),
        id: WORKFLOW_TYPE.PERIODIC,
      },
      {
        text: (
          <span className="text-sm font-semibold">
            Reporting ({filterFunc(WORKFLOW_TYPE.SUBSCRIPTION)})
          </span>
        ),
        id: WORKFLOW_TYPE.SUBSCRIPTION,
      },
      {
        text: (
          <span className="text-sm font-semibold">
            Signal alert ({filterFunc(WORKFLOW_TYPE.SIGNAL_ALERT)})
          </span>
        ),
        id: WORKFLOW_TYPE.SIGNAL_ALERT,
      },
    ];
  }, [data?.length, filterFunc]);

  const [selectedTab, selectTab] = useTabs(tabs, 'tab');

  const onToggleHandler = async (alias: string, status: boolean) => {
    await service.workflow.toggleWorkflow(alias, status);
    queryClient.setQueryData(
      notificationQueryKeys.list(workflowTypes),
      (old: any) =>
        old?.map((workflow: Workflow) =>
          workflow.alias === alias
            ? {
                ...workflow,
                enable: status,
              }
            : workflow,
        ),
    );
  };

  const onSelectHandler = (workflow: Workflow) => {
    wr.push(`/workflow/${workflow.alias}`);
  };

  const onCreateHandler = useCallback(
    (type: WORKFLOW_TYPE, basis?: WORKFLOW_SUBSCRIPTION_BASIS) => {
      if (filterFunc(type) >= 5) {
        const errorMsg = getApiErrorMessage(
          'notification__not_enough_capacity',
        );
        addTaost(errorMsg, 'fail');
        return;
      }
      switch (type) {
        case WORKFLOW_TYPE.PERIODIC:
          wr.push('/workflow/create-periodic');
          break;
        case WORKFLOW_TYPE.REALTIME:
          wr.push('/workflow/create-realtime');
          break;
        case WORKFLOW_TYPE.SUBSCRIPTION:
          if (basis === WORKFLOW_SUBSCRIPTION_BASIS.DASHBOARD2) {
            wr.push('/workflow/create-subscription2');
          } else {
            wr.push('/workflow/create-subscription');
          }
          break;
        case WORKFLOW_TYPE.SIGNAL_ALERT:
          wr.push('/workflow/create-signal-alert');
          break;
      }
    },
    [filterFunc],
  );

  return (
    <div className="flex h-screen flex-col overflow-auto">
      <PageHeader
        title="Workflows"
        desc="Build workflows to notify individuals and teams in your organization."
      />
      <div className="flex flex-1 flex-col overflow-auto px-8">
        <h2 className="text-base font-semibold">Create a workflow</h2>
        <div className="h-3" />
        <div className="flex gap-2">
          <QuickActionItem<number>
            value={WORKFLOW_TYPE.REALTIME}
            icon={WorkflowIconMap[WORKFLOW_TYPE.REALTIME]}
            title="New feedback received"
            description="Real-time"
            onClick={onCreateHandler}
            addonText={`${filterFunc(
              WORKFLOW_TYPE.REALTIME,
            )}/${MAX_WORKFLOW_COUNT}`}
          />
          <QuickActionItem<number>
            value={WORKFLOW_TYPE.PERIODIC}
            icon={WorkflowIconMap[WORKFLOW_TYPE.PERIODIC]}
            title="Metric monitoring"
            description="Schedule"
            onClick={onCreateHandler}
            addonText={`${filterFunc(
              WORKFLOW_TYPE.PERIODIC,
            )}/${MAX_WORKFLOW_COUNT}`}
          />

          <QuickActionItem<number>
            value={WORKFLOW_TYPE.SUBSCRIPTION}
            icon={WorkflowIconMap[WORKFLOW_TYPE.SUBSCRIPTION]}
            title="Report scheduled"
            description="Subscription"
            onClick={(type) =>
              onCreateHandler(type, WORKFLOW_SUBSCRIPTION_BASIS.DASHBOARD2)
            }
            addonText={`${filterFunc(
              WORKFLOW_TYPE.SUBSCRIPTION,
            )}/${MAX_WORKFLOW_COUNT}`}
            tooltip={{
              node: m.workflow('WORKFLOW_DESCRIPTION'),
            }}
          />
          <QuickActionItem<number>
            value={WORKFLOW_TYPE.SIGNAL_ALERT}
            icon={WorkflowIconMap[WORKFLOW_TYPE.SIGNAL_ALERT]}
            title="Signal alert"
            description="By condition"
            onClick={onCreateHandler}
            addonText={`${filterFunc(
              WORKFLOW_TYPE.SIGNAL_ALERT,
            )}/${MAX_WORKFLOW_COUNT}`}
          />
        </div>
        <div className="h-10" />
        <Tabs tabs={tabs} selectedTabId={selectedTab}>
          <Tabs.TabHeader tabColor="primary" tabSize="m" onSelect={selectTab} />
        </Tabs>
        <div className="h-5" />
        <List
          className="gap-2 flex-1 pb-12"
          data={data?.filter((w) => {
            if (selectedTab === 'all') return true;
            return w.type === selectedTab;
          })}
          render={(workflow) => (
            <WorkflowItem
              key={workflow.alias}
              {...(workflow as Workflow)}
              onClick={onSelectHandler}
              onToggle={onToggleHandler}
              style={{ maxWidth: 1000, width: '100%' }}
            />
          )}
          placeholder={{
            node: (
              <div className="flex h-[300px] items-center justify-center">
                <span className="text-sm font-semibold text-muted-foreground">
                  Create a new workflow
                </span>
              </div>
            ),
          }}
        />
      </div>
    </div>
  );
};

export default WorkflowList;
